'use client';

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'user_language';
const DEFAULT_LANG = 'en';
const CHANGE_EVENT = 'user-language-change';

type SupportedLang = 'en' | 'de';

const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);

  function handleStorageEvent(e: StorageEvent) {
    if (e.key === STORAGE_KEY) callback();
  }

  function handleCustomEvent() {
    callback();
  }

  window.addEventListener('storage', handleStorageEvent);
  window.addEventListener(CHANGE_EVENT, handleCustomEvent);

  return () => {
    listeners.delete(callback);
    window.removeEventListener('storage', handleStorageEvent);
    window.removeEventListener(CHANGE_EVENT, handleCustomEvent);
  };
}

function getSnapshot(): SupportedLang {
  const val = localStorage.getItem(STORAGE_KEY);
  return val === 'de' ? 'de' : DEFAULT_LANG;
}

function getServerSnapshot(): SupportedLang {
  return DEFAULT_LANG;
}

/**
 * Notify same-tab listeners that the user language changed.
 * Call this after `localStorage.setItem('user_language', lang)`.
 */
export function notifyLanguageChange() {
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * Reactive hook that returns the current user language ('en' | 'de').
 * Re-renders when the language is changed via notifyLanguageChange()
 * or from another tab via the storage event.
 */
export function useUserLanguage(): SupportedLang {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
